

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "../dashboard/components/Sidebar";
import Topbar from "./Topbar";
import CommandPalette from "./CommandPalette";
import { useAuth } from "@/hooks/useAuth";
import { usePWA } from "@/hooks/usePWA";
import AccountSettingsModal from "./AccountSettingsModal";

// Error boundary for dashboard
function DashboardErrorFallback({ error }: { error: Error }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-stone-800 mb-2">Something went wrong</h2>
        <p className="text-stone-500 mb-4">{error.message}</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
        >
          Reload Page
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { user, logout, loading } = useAuth();
  const { isStandalone } = usePWA();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Client-side auth check - replaces middleware for static export
  useEffect(() => {
    if (!user && !loading) {
      const currentPath = window.location.pathname;
      const loginUrl = currentPath !== '/login' ? `/login?redirect=${encodeURIComponent(currentPath)}` : '/login';
      navigate(loginUrl, { replace: true });
    }
  }, [user, loading, navigate]);

  // Catch any rendering errors
  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      console.error('Dashboard error:', e.error);
      setError(e.error);
    };
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (error) {
    return <DashboardErrorFallback error={error} />;
  }

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setIsSidebarOpen(false);
  const toggleSidebarCollapse = () => setIsSidebarCollapsed(!isSidebarCollapsed);

  // Global Ctrl+K / Cmd+K shortcut for command palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);
  
  const handleLogout = async () => {
    await logout();
    () => navigate(-1);
    navigate("/login");
  };

  // Handle pull-to-refresh for PWA standalone mode
  useEffect(() => {
    if (!isStandalone) return;

    let touchStartY = 0;
    let touchEndY = 0;
    const minSwipeDistance = 100;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      touchEndY = e.touches[0].clientY;
      
      // Check if at top of page and pulling down
      if (window.scrollY === 0 && touchEndY > touchStartY) {
        const pullDistance = touchEndY - touchStartY;
        if (pullDistance > 50 && pullDistance < minSwipeDistance) {
          // Show subtle pull indicator
          document.body.style.overscrollBehavior = 'contain';
        }
      }
    };

    const handleTouchEnd = async () => {
      const swipeDistance = touchEndY - touchStartY;
      
      // Pull to refresh when at top of page
      if (window.scrollY === 0 && swipeDistance > minSwipeDistance && !isRefreshing) {
        setIsRefreshing(true);
        // Reload the page to refresh data
        window.location.reload();
      }
      
      document.body.style.overscrollBehavior = '';
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isStandalone, isRefreshing]);

  return (
    <div className={`flex h-screen overflow-hidden ${isStandalone ? 'pwa-standalone' : ''}`}>
      {/* Pull to refresh indicator */}
      {isStandalone && isRefreshing && (
        <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center py-2 bg-amber-500 text-white text-sm font-medium">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Refreshing...
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}
      
      <Sidebar 
        userName={user?.name || "User"} 
        userRole={user?.role || "user"}
        onLogout={handleLogout}
        isOpen={isSidebarOpen}
        onClose={closeSidebar}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapse}
      />
      <div className={`flex-1 flex flex-col overflow-hidden min-w-0 transition-all duration-300 ${isStandalone ? 'pt-safe' : ''} ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <Topbar
          onMenuClick={toggleSidebar}
          isSidebarOpen={isSidebarOpen}
          onAccountSettings={() => setShowAccountSettings(true)}
          onOpenCommandPalette={() => setShowCommandPalette(true)}
        />
        <main className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-6">
          {children}
        </main>
      </div>
      
      {/* Account Settings Modal */}
      <AccountSettingsModal
        isOpen={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
      />

      {/* Command Palette */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
      />
    </div>
  );
}
